import React from "react";
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import Modal from "./Modal";
import ProductModal from "./ProductModal";
import CheckoutModal from "./CheckoutModal";
import PageContent from "./PageContent";
import Footer from "./Footer";
import { Helmet, HelmetProvider } from "react-helmet-async";
import SellYourKicks from "./SellYourKicks";

const AppLayout = ({
  isMenuOpen,
  setIsMenuOpen,
  menuRef,
  handleCheckoutClick,
  cartItems,
  setActivePage,
  showProductModal,
  handleCloseModal,
  selectedProduct,
  currentImageIndex,
  getImagesArray,
  handlePrevImage,
  handleNextImage,
  setCurrentImageIndex,
  handleAddToCart,
  showCheckoutModal,
  checkoutStep,
  checkoutTitles,
  customerInfo,
  showBilling,
  setShowBilling,
  handleCustomerInfoChange,
  handleBillingCheckboxChange,
  isValidEmail,
  isValidUSPhone,
  isValidAddress,
  isFormValid,
  isBillingValid,
  handlePrevStep,
  handleNextStep,
  loading,
  stripeReady,
  stripeComplete,
  clientSecret,
  handleStripeReady,
  handleStripeChange,
  checkoutError,
  setCheckoutError,
  updateInventory,
  storeCustomerInfo,
  setShowPaymentSuccess,
  setCheckoutStep,
  totalCartPrice,
  handleRemoveFromCart,
  handleProductClick,
  showPaymentSuccess,
  handlePaymentSuccessAndInventory,
  products,
  services, // <-- add this
  activePage,
  setShowProductModal,
  setShowCheckoutModal,
  handleUpdateCartQuantity,
  onShowSizeModal,
  selectedSizes,
  handleUpdatePairsToShip, // <-- add this line
  taxAmount,
  finalShippingCost,
  finalTaxAmount,
  finalProcessingFee,
  finalTotalWithFee,
  paymentIntentReady,
  setClientSecret,
  setFinalShippingCost,
  setFinalTaxAmount,
  setFinalProcessingFee,
  setFinalTotalWithFee,
  setPaymentIntentReady,
}) => {
  // Always call hooks at the top level, never conditionally
  const navigate = useNavigate();

  // Helper to handle navigation and activePage
  const handleNav = (page, path) => {
    setActivePage(page);
    navigate(path);
  };

  return (
    <HelmetProvider>
      <div className="font-sans antialiased text-gray-800 bg-gray-100 min-h-screen">
        <style>
          {`
          body { font-family: 'Inter', sans-serif; }
          .lucide-icon { display: inline-block; vertical-align: middle; }
        `}
        </style>
        <header className="bg-white shadow-md rounded-b-3xl sticky top-0 z-40">
          <nav className="container mx-auto px-4 py-4 flex justify-between items-center relative">
            <div className="text-2xl font-bold text-black flex items-center">
              <img
                src="./kwc_logo_192.png"
                alt="Logo"
                className="w-20 h-20 mr-10 object-cover cursor-pointer"
                onClick={() => handleNav("home", "/")}
                loading="eager"
              />
              Buy | Sell | Restore
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleCheckoutClick}
                className="text-gray-600 p-2 focus:outline-none relative"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide-icon"
                >
                  <circle cx="9" cy="21" r="1"></circle>
                  <circle cx="20" cy="21" r="1"></circle>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61l1.38-7.39H6"></path>
                </svg>
                {cartItems.length > 0 && (
                  <span className="absolute top-0 right-0 -mt-1 -mr-1 bg-red-500 text-white text-xs font-bold rounded-full px-2 py-1">
                    {cartItems.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="hamburger-btn text-gray-600 p-2 focus:outline-none relative"
              >
                <span className="block w-6 h-0.5 bg-gray-600 rounded transition-transform duration-300"></span>
                <span className="block w-6 h-0.5 bg-gray-600 rounded transition-transform duration-300 my-1"></span>
                <span className="block w-6 h-0.5 bg-gray-600 rounded transition-transform duration-300"></span>
              </button>
            </div>
            {isMenuOpen && (
              <div
                ref={menuRef}
                className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-lg z-50"
              >
                <button
                  onClick={() => {
                    handleNav("home", "/");
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-100 transition-colors"
                  type="button"
                >
                  Products
                </button>
                <button
                  onClick={() => {
                    handleNav("reice", "/icing");
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-100 transition-colors"
                  type="button"
                >
                  Re-Icing Service
                </button>
                <button
                  onClick={() => {
                    handleNav("sellyourkicks", "/sellyourkicks");
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-100 transition-colors"
                  type="button"
                >
                  Sell Your Shoes
                </button>
                <button
                  onClick={() => {
                    handleNav("about", "/about");
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-100 transition-colors"
                  type="button"
                >
                  About Us
                </button>
                <button
                  onClick={() => {
                    handleNav("contact", "/contact");
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-100 transition-colors"
                  type="button"
                >
                  Contact
                </button>
                <button
                  onClick={() => {
                    handleNav("faq", "/faq");
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-100 transition-colors"
                  type="button"
                >
                  FAQ
                </button>
              </div>
            )}
          </nav>
        </header>
        <main className="container mx-auto px-4 py-4 mt-4">
          <Routes>
            <Route
              path="/"
              element={
                <>
                  <Helmet>
                    <title>Kickin' w/ Crews | Shop Our Inventory</title>
                    <meta
                      name="description"
                      content="Shop the latest inventory at Kickin' w/ Crews."
                    />
                    <link rel="canonical" href="https://kickinwcrews.com/" />
                    {/* Open Graph */}
                    <meta
                      property="og:title"
                      content="Kickin' w/ Crews | Shop Our Inventory"
                    />
                    <meta
                      property="og:description"
                      content="Shop the latest inventory at Kickin' w/ Crews."
                    />
                    <meta
                      property="og:url"
                      content="https://kickinwcrews.com/"
                    />
                    <meta property="og:type" content="website" />
                    {/* Twitter */}
                    <meta
                      name="twitter:title"
                      content="Kickin' w/ Crews | Shop Our Inventory"
                    />
                    <meta
                      name="twitter:description"
                      content="Shop the latest inventory at Kickin' w/ Crews."
                    />
                  </Helmet>
                  {/* Show heading only on home page */}
                  {activePage === "home" && (
                    <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-4 leading-tight text-center">
                      Shop Our Inventory
                    </h1>
                  )}

                  {/* Filter by Size Button aligned right under the heading, only on home page */}
                  {onShowSizeModal && activePage === "home" && (
                    <div className="flex justify-end mb-6">
                      <button
                        onClick={onShowSizeModal}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-full font-semibold shadow hover:bg-indigo-700"
                      >
                        {selectedSizes && selectedSizes.length
                          ? `Sizes: ${selectedSizes.join(", ")}`
                          : "Filter by Size"}
                      </button>
                    </div>
                  )}
                  <PageContent
                    activePage={activePage}
                    products={products}
                    services={services}
                    getImagesArray={getImagesArray}
                    handleProductClick={handleProductClick}
                    loading={!products || products.length === 0}
                  />
                </>
              }
            />
            <Route
              path="/icing"
              element={
                <>
                  <Helmet>
                    <title>Re-Icing Service | Kickin' w/ Crews</title>
                    <meta
                      name="description"
                      content="Restore your sneakers with our professional re-icing service."
                    />
                    <link
                      rel="canonical"
                      href="https://kickinwcrews.com/icing"
                    />
                    {/* Open Graph */}
                    <meta
                      property="og:title"
                      content="Re-Icing Service | Kickin' w/ Crews"
                    />
                    <meta
                      property="og:description"
                      content="Restore your sneakers with our professional re-icing service."
                    />
                    <meta
                      property="og:url"
                      content="https://kickinwcrews.com/icing"
                    />
                    <meta property="og:type" content="website" />
                    {/* Twitter */}
                    <meta
                      name="twitter:title"
                      content="Re-Icing Service | Kickin' w/ Crews"
                    />
                    <meta
                      name="twitter:description"
                      content="Restore your sneakers with our professional re-icing service."
                    />
                  </Helmet>
                  <PageContent
                    activePage="reice"
                    products={products}
                    services={services}
                    getImagesArray={getImagesArray}
                    handleProductClick={handleProductClick}
                    loading={false}
                  />
                </>
              }
            />
            <Route
              path="/about"
              element={
                <>
                  <Helmet>
                    <title>About Us | Kickin' w/ Crews</title>
                    <meta
                      name="description"
                      content="Learn more about Kickin' w/ Crews and our mission."
                    />
                    <link
                      rel="canonical"
                      href="https://kickinwcrews.com/about"
                    />
                    {/* Open Graph */}
                    <meta
                      property="og:title"
                      content="About Us | Kickin' w/ Crews"
                    />
                    <meta
                      property="og:description"
                      content="Learn more about Kickin' w/ Crews and our mission."
                    />
                    <meta
                      property="og:url"
                      content="https://kickinwcrews.com/about"
                    />
                    <meta property="og:type" content="website" />
                    {/* Twitter */}
                    <meta
                      name="twitter:title"
                      content="About Us | Kickin' w/ Crews"
                    />
                    <meta
                      name="twitter:description"
                      content="Learn more about Kickin' w/ Crews and our mission."
                    />
                  </Helmet>
                  <PageContent
                    activePage="about"
                    products={products}
                    services={services}
                    getImagesArray={getImagesArray}
                    handleProductClick={handleProductClick}
                    loading={false}
                  />
                </>
              }
            />
            <Route
              path="/contact"
              element={
                <>
                  <Helmet>
                    <title>Contact Us | Kickin' w/ Crews</title>
                    <meta
                      name="description"
                      content="Get in touch with Kickin' w/ Crews for any inquiries."
                    />
                    <link
                      rel="canonical"
                      href="https://kickinwcrews.com/contact"
                    />
                    {/* Open Graph */}
                    <meta
                      property="og:title"
                      content="Contact Us | Kickin' w/ Crews"
                    />
                    <meta
                      property="og:description"
                      content="Get in touch with Kickin' w/ Crews for any inquiries."
                    />
                    <meta
                      property="og:url"
                      content="https://kickinwcrews.com/contact"
                    />
                    <meta property="og:type" content="website" />
                    {/* Twitter */}
                    <meta
                      name="twitter:title"
                      content="Contact Us | Kickin' w/ Crews"
                    />
                    <meta
                      name="twitter:description"
                      content="Get in touch with Kickin' w/ Crews for any inquiries."
                    />
                  </Helmet>
                  <PageContent
                    activePage="contact"
                    products={products}
                    services={services}
                    getImagesArray={getImagesArray}
                    handleProductClick={handleProductClick}
                    loading={false}
                  />
                </>
              }
            />
            <Route
              path="/faq"
              element={
                <>
                  <Helmet>
                    <title>FAQ | Kickin' w/ Crews</title>
                    <meta
                      name="description"
                      content="Frequently asked questions about Kickin' w/ Crews."
                    />
                    <link rel="canonical" href="https://kickinwcrews.com/faq" />
                    {/* Open Graph */}
                    <meta
                      property="og:title"
                      content="FAQ | Kickin' w/ Crews"
                    />
                    <meta
                      property="og:description"
                      content="Frequently asked questions about Kickin' w/ Crews."
                    />
                    <meta
                      property="og:url"
                      content="https://kickinwcrews.com/faq"
                    />
                    <meta property="og:type" content="website" />
                    {/* Twitter */}
                    <meta
                      name="twitter:title"
                      content="FAQ | Kickin' w/ Crews"
                    />
                    <meta
                      name="twitter:description"
                      content="Frequently asked questions about Kickin' w/ Crews."
                    />
                  </Helmet>
                  <PageContent
                    activePage="faq"
                    products={products}
                    services={services}
                    getImagesArray={getImagesArray}
                    handleProductClick={handleProductClick}
                    loading={false}
                  />
                </>
              }
            />
            <Route
              path="/sellyourkicks"
              element={
                <>
                  <Helmet>
                    <title>Sell Your Shoes | Kickin' w/ Crews</title>
                    <meta
                      name="description"
                      content="Sell your new or gently used shoes to Kickin' w/ Crews. Fast response and easy process!"
                    />
                    <link
                      rel="canonical"
                      href="https://kickinwcrews.com/sellyourkicks"
                    />
                    <meta
                      property="og:title"
                      content="Sell Your Shoes | Kickin' w/ Crews"
                    />
                    <meta
                      property="og:description"
                      content="Sell your new or gently used shoes to Kickin' w/ Crews. Fast response and easy process!"
                    />
                    <meta
                      property="og:url"
                      content="https://kickinwcrews.com/sellyourkicks"
                    />
                    <meta property="og:type" content="website" />
                    <meta
                      name="twitter:title"
                      content="Sell Your Shoes | Kickin' w/ Crews"
                    />
                    <meta
                      name="twitter:description"
                      content="Sell your new or gently used shoes to Kickin' w/ Crews. Fast response and easy process!"
                    />
                  </Helmet>
                  <SellYourKicks />
                </>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        {/* Product Modal */}
        <Modal
          show={showProductModal}
          onClose={handleCloseModal}
          compact={selectedProduct && !selectedProduct.size}
        >
          {selectedProduct && (
            <ProductModal
              selectedProduct={selectedProduct}
              currentImageIndex={currentImageIndex}
              imagesArray={getImagesArray(selectedProduct)}
              handlePrevImage={handlePrevImage}
              handleNextImage={handleNextImage}
              setCurrentImageIndex={setCurrentImageIndex}
              handleAddToCart={handleAddToCart}
              cartItems={cartItems}
              setShowProductModal={setShowProductModal}
              setShowCheckoutModal={setShowCheckoutModal}
            />
          )}
        </Modal>
        {/* Checkout Modal */}
        <Modal show={showCheckoutModal} onClose={handleCloseModal}>
          <CheckoutModal
            checkoutStep={checkoutStep}
            checkoutTitles={checkoutTitles}
            cartItems={cartItems}
            customerInfo={customerInfo}
            showBilling={showBilling}
            setShowBilling={setShowBilling}
            handleCustomerInfoChange={handleCustomerInfoChange}
            handleBillingCheckboxChange={handleBillingCheckboxChange}
            isValidEmail={isValidEmail}
            isValidUSPhone={isValidUSPhone}
            isValidAddress={isValidAddress}
            isFormValid={isFormValid}
            isBillingValid={isBillingValid}
            handlePrevStep={handlePrevStep}
            handleNextStep={handleNextStep}
            loading={loading}
            stripeReady={stripeReady}
            stripeComplete={stripeComplete}
            clientSecret={clientSecret}
            showCheckoutModal={showCheckoutModal}
            handleStripeReady={handleStripeReady}
            handleStripeChange={handleStripeChange}
            checkoutError={checkoutError}
            setCheckoutError={setCheckoutError}
            updateInventory={updateInventory}
            storeCustomerInfo={storeCustomerInfo}
            setShowPaymentSuccess={setShowPaymentSuccess}
            setCheckoutStep={setCheckoutStep}
            totalCartPrice={totalCartPrice}
            handleRemoveFromCart={handleRemoveFromCart}
            handleProductClick={handleProductClick}
            handleUpdateCartQuantity={handleUpdateCartQuantity}
            handleUpdatePairsToShip={handleUpdatePairsToShip}
            taxAmount={taxAmount}
            finalShippingCost={finalShippingCost}
            finalTaxAmount={finalTaxAmount}
            finalProcessingFee={finalProcessingFee}
            finalTotalWithFee={finalTotalWithFee}
            paymentIntentReady={paymentIntentReady}
            services={services}
            setClientSecret={setClientSecret}
            setFinalShippingCost={setFinalShippingCost}
            setFinalTaxAmount={setFinalTaxAmount}
            setFinalProcessingFee={setFinalProcessingFee}
            setFinalTotalWithFee={setFinalTotalWithFee}
            setPaymentIntentReady={setPaymentIntentReady}
          />
        </Modal>
        {/* Payment Success Modal */}
        <Modal
          show={showPaymentSuccess}
          onClose={handlePaymentSuccessAndInventory}
        >
          <div className="flex flex-col items-center justify-center py-12">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="64"
              height="64"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide-icon text-green-500 mb-4"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <h3 className="text-2xl font-bold text-green-600 mb-4">
              Payment Successful!
            </h3>
            <p className="text-lg text-gray-700 mb-4 text-center">
              Thank you for shopping with Kickin' w/ Crews!
              <br />
              You will receive a confirmation email shortly.
            </p>
            <button
              onClick={handlePaymentSuccessAndInventory}
              className="py-3 px-8 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 transition-colors font-bold text-xl mt-4"
            >
              OK
            </button>
          </div>
        </Modal>
        {/* Footer */}
        <Footer />
      </div>
    </HelmetProvider>
  );
};

export default AppLayout;
